import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  X, 
  Filter, 
  Layers, 
  ArrowUpDown, 
  RotateCcw, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  AlertCircle, 
  Package 
} from 'lucide-react';
import './Products.css';
import { handleGetApi, handlePostApi } from '../../api/api';
import Cookies from 'js-cookie';

const Products = () => {
  const navigate = useNavigate();
  const token = Cookies.get('auth_token') ? JSON.parse(Cookies.get('auth_token')) : '';
  const headers = {
    Authorization: token
  };

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters State
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // =========================================================
  // FETCH DATA
  // =========================================================
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await handleGetApi('admin/get/product', headers);
      if (res.success || Array.isArray(res.data)) {
        setProducts(res.data || []);
      }
    } catch (err) {
      console.error('Fetch Products Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await handleGetApi('admin/get/category', headers);
      if (res.success || Array.isArray(res.data)) {
        setCategories(res.data || []);
      }
    } catch (err) {
      console.error('Fetch Categories Error:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================================================
  // DELETE PRODUCT
  // =========================================================
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const res = await handlePostApi('admin/delete/product', { id }, headers);
        if (res.success) {
          setProducts((prev) => prev.filter((p) => p.id !== id));
        } else {
          alert(res.message || 'Failed to delete product');
        }
      } catch (err) {
        console.error('Delete Error:', err);
      }
    }
  };

  // =========================================================
  // FILTER & SORT PRODUCTS
  // =========================================================
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // 1. Search Query
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const name = (item?.name || '').toLowerCase();
        const cat = (item?.category_name || '').toLowerCase();
        const subCat = (item?.subcategory_name || '').toLowerCase();
        const sku = (item?.variants?.[0]?.sku || '').toLowerCase();
        return name.includes(q) || cat.includes(q) || subCat.includes(q) || sku.includes(q);
      });
    }

    // 2. Category Filter
    if (categoryFilter) {
      filtered = filtered.filter((item) => {
        return (
          String(item.category_id) === String(categoryFilter) ||
          (item.category_name || '').toLowerCase() === categoryFilter.toLowerCase()
        );
      });
    }

    // 3. Status Filter
    if (statusFilter) {
      filtered = filtered.filter((item) => {
        return (item.status || '').toLowerCase() === statusFilter.toLowerCase();
      });
    }

    // 4. Sort By
    filtered.sort((a, b) => {
      const priceA = Number(a?.variants?.[0]?.price || 0);
      const priceB = Number(b?.variants?.[0]?.price || 0);
      const nameA = (a?.name || '').toLowerCase();
      const nameB = (b?.name || '').toLowerCase();

      if (sortBy === 'newest') {
        return new Date(b.created_at || b.id || 0) - new Date(a.created_at || a.id || 0);
      } else if (sortBy === 'oldest') {
        return new Date(a.created_at || a.id || 0) - new Date(b.created_at || b.id || 0);
      } else if (sortBy === 'price_high') {
        return priceB - priceA;
      } else if (sortBy === 'price_low') {
        return priceA - priceB;
      } else if (sortBy === 'name_asc') {
        return nameA.localeCompare(nameB);
      } else if (sortBy === 'name_desc') {
        return nameB.localeCompare(nameA);
      }
      return 0;
    });

    return filtered;
  }, [products, search, categoryFilter, statusFilter, sortBy]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter, statusFilter, sortBy, itemsPerPage]);

  // =========================================================
  // PAGINATION
  // =========================================================
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage) || 1;
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(start, start + itemsPerPage);
  }, [filteredProducts, currentPage, itemsPerPage]);

  const hasActiveFilters = Boolean(search.trim() || categoryFilter || statusFilter || sortBy !== 'newest');

  const handleResetFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setStatusFilter('');
    setSortBy('newest');
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  return (
    <MainLayout title="Product Management">
      <div className="products-container fade-in">
        {/* HEADER CONTROLS & FILTERS */}
        <div className="products-header-bar">
          <div className="search-input-box">
            <Search size={18} className="search-icon" />
            <input 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              type="text" 
              placeholder="Search products by name, category, SKU..." 
            />
            {search && (
              <button 
                type="button" 
                className="clear-search-btn" 
                onClick={() => setSearch('')}
                title="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="products-filters-row">
            {/* Category Filter */}
            <div className="filter-dropdown-wrapper">
              <Layers size={15} className="dropdown-icon" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Category: All</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="filter-dropdown-wrapper">
              <Filter size={15} className="dropdown-icon" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Status: All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Sort Dropdown */}
            <div className="filter-dropdown-wrapper">
              <ArrowUpDown size={15} className="dropdown-icon" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="name_asc">Name: A to Z</option>
                <option value="name_desc">Name: Z to A</option>
              </select>
            </div>

            {/* Reset Filters */}
            {hasActiveFilters && (
              <button
                type="button"
                className="reset-btn"
                onClick={handleResetFilters}
                title="Reset all filters"
              >
                <RotateCcw size={14} />
                <span>Reset</span>
              </button>
            )}

            {/* Add Product Button */}
            <button className="add-product-btn" onClick={() => navigate('/products/add')}>
              <Plus size={18} />
              <span>Add Product</span>
            </button>
          </div>
        </div>

        {/* RESULTS COUNT & SUMMARY */}
        <div className="products-count-bar">
          <span>
            Showing <strong>{filteredProducts.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}</strong> to{' '}
            <strong>{Math.min(currentPage * itemsPerPage, filteredProducts.length)}</strong> of{' '}
            <strong>{filteredProducts.length}</strong> products
          </span>
          {hasActiveFilters && (
            <span className="filter-active-pill">
              Filtered
            </span>
          )}
        </div>

        {/* PRODUCTS TABLE CARD */}
        <div className="products-card">
          <div className="table-responsive">
            <table className="products-table">
              <thead>
                <tr>
                  <th className="th-product">Product</th>
                  <th className="th-category">Category</th>
                  <th className="th-sku">SKU</th>
                  <th className="th-price">Price</th>
                  <th className="th-status text-center">Status</th>
                  <th className="th-actions text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6">
                      <div className="products-empty-state">
                        <Clock size={24} className="spin-icon" />
                        <p>Loading products...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      <div className="products-empty-state">
                        <AlertCircle size={28} />
                        <h4>No products found</h4>
                        <p>No products matched your search or active filter criteria.</p>
                        {hasActiveFilters && (
                          <button
                            type="button"
                            className="clear-filters-action-btn"
                            onClick={handleResetFilters}
                          >
                            <RotateCcw size={14} />
                            <span>Clear Filters</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedProducts.map((product) => (
                    <tr key={product.id}>
                      <td className="product-cell">
                        {product?.variants?.[0]?.thumbnail ? (
                          <img 
                            src={`${process.env.REACT_APP_IMAGE_URL}variants/${product?.variants[0]?.thumbnail}`} 
                            alt={product.name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }} 
                          />
                        ) : (
                          <div className="product-no-img">
                            <Package size={20} />
                          </div>
                        )}
                        <div className="product-info">
                          <span className="product-name">{product.name}</span>
                          <span className="product-cat-small">
                            {product.category_name} {product.subcategory_name && `> ${product.subcategory_name}`}
                          </span>
                        </div>
                      </td>

                      <td className="td-category">{product.category_name || '-'}</td>

                      <td className="td-sku">
                        <code className="sku-code">{product.variants?.[0]?.sku || 'N/A'}</code>
                      </td>

                      <td className="td-price product-price">
                        ₹{Number(product.variants?.[0]?.price || 0).toLocaleString('en-IN')}
                      </td>

                      <td className="td-status text-center">
                        <span className={`status-pill ${(product.status || 'inactive').toLowerCase()}`}>
                          {product.status || 'Inactive'}
                        </span>
                      </td>

                      <td className="td-actions text-right">
                        <div className="action-btns-row">
                          <button 
                            className="edit-btn" 
                            title="Edit Product"
                            onClick={() => navigate(`/products/edit/${product.id}`)}
                          >
                            <Edit size={16} />
                          </button>
                          <button 
                            className="delete-btn" 
                            title="Delete Product"
                            onClick={() => handleDelete(product.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION BAR */}
          {!loading && filteredProducts.length > 0 && (
            <div className="pagination-wrapper">
              <div className="per-page-selector">
                <span>Show:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              <div className="pagination-nav">
                <button
                  type="button"
                  className="page-nav-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  title="Previous Page"
                >
                  <ChevronLeft size={16} />
                  <span>Prev</span>
                </button>

                <div className="page-numbers">
                  {getPageNumbers().map((page, idx) => (
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="page-ellipsis">...</span>
                    ) : (
                      <button
                        key={page}
                        type="button"
                        className={`page-num-btn ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                <button
                  type="button"
                  className="page-nav-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  title="Next Page"
                >
                  <span>Next</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Products;
